import React, { useState } from 'react';
import { ConfigDocument, CredentialSet } from '../../types';

interface CredentialsTabProps {
  config: ConfigDocument | null;
  onSave: (config: ConfigDocument) => Promise<void>;
  loading: boolean;
}

interface FormData {
  name: string;
  type: 'smb' | 'sharepoint';
  username: string;
  password: string;
  tenantId: string;
  clientId: string;
  clientSecret: string;
}

interface ValidationErrors {
  name?: string;
  type?: string;
  username?: string;
  password?: string;
  tenantId?: string;
  clientId?: string;
  clientSecret?: string;
}

const EMPTY_FORM: FormData = {
  name: '',
  type: 'smb',
  username: '',
  password: '',
  tenantId: '',
  clientId: '',
  clientSecret: '',
};

const CredentialsTab: React.FC<CredentialsTabProps> = ({ config, onSave, loading }) => {
  const [editing, setEditing] = useState<string | null>(null); // key of credential being edited, or 'new'
  const [formData, setFormData] = useState<FormData>({ ...EMPTY_FORM });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Get credentials as array of [key, value]
  const credentials: [string, CredentialSet][] = config
    ? Object.entries(config.credentialSets).sort(([a], [b]) => a.localeCompare(b))
    : [];

  // Find destinations referencing a given credential set key (both SMB and digital)
  const getDestinationsUsingCredential = (credKey: string): string[] => {
    if (!config) return [];
    const smbDests = Object.entries(config.smbDestinations)
      .filter(([, dest]) => dest.credentialSetName === credKey)
      .map(([destName]) => destName);
    const digitalDests = Object.entries(config.digitalDestinations || {})
      .filter(([, dest]) => dest.credentialSetName === credKey)
      .map(([destName]) => `${destName} (digital)`);
    return [...smbDests, ...digitalDests];
  };

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Credential set name is required';
    } else if (
      editing === 'new' &&
      config?.credentialSets &&
      formData.name.trim() in config.credentialSets
    ) {
      newErrors.name = 'A credential set with this name already exists';
    }

    if (!formData.username.trim()) {
      newErrors.username = formData.type === 'sharepoint'
        ? 'App registration name is required'
        : 'Username is required';
    }

    if (formData.type === 'smb') {
      // Password required only for new SMB credentials
      if (editing === 'new' && !formData.password) {
        newErrors.password = 'Password is required';
      }
    } else {
      // SharePoint fields required for new credentials
      if (editing === 'new') {
        if (!formData.tenantId.trim()) newErrors.tenantId = 'Tenant ID is required';
        if (!formData.clientId.trim()) newErrors.clientId = 'Client ID is required';
        if (!formData.clientSecret.trim()) newErrors.clientSecret = 'Client secret is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddNew = () => {
    setEditing('new');
    setFormData({ ...EMPTY_FORM });
    setErrors({});
  };

  const handleEdit = (key: string) => {
    const cred = config?.credentialSets[key];
    if (!cred) return;
    setEditing(key);
    setFormData({
      name: key,
      type: (cred.type as 'smb' | 'sharepoint') || 'smb',
      username: cred.username,
      password: '',
      tenantId: '',
      clientId: '',
      clientSecret: '',
    });
    setErrors({});
  };

  const handleCancel = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM });
    setErrors({});
  };

  const handleSave = async () => {
    if (!validate() || !config) return;

    setSaving(true);
    try {
      const updatedCredentials = { ...config.credentialSets };

      // If editing and name changed, remove old key
      if (editing !== 'new' && editing !== formData.name.trim()) {
        delete updatedCredentials[editing!];
      }

      const existing = editing !== 'new' ? config.credentialSets[editing!] : undefined;

      const credentialSet: CredentialSet = {
        type: formData.type,
        username: formData.username.trim(),
        secretArn: existing?.secretArn,
        lastUpdated: new Date().toISOString(),
      };

      if (formData.type === 'smb') {
        // Only include password if provided (new credential or password change)
        if (formData.password) {
          credentialSet.password = formData.password;
        }
      } else {
        // SharePoint: include fields if provided (new credential or updating secrets)
        if (formData.tenantId.trim()) credentialSet.tenantId = formData.tenantId.trim();
        if (formData.clientId.trim()) credentialSet.clientId = formData.clientId.trim();
        if (formData.clientSecret.trim()) credentialSet.clientSecret = formData.clientSecret.trim();
      }

      updatedCredentials[formData.name.trim()] = credentialSet;

      await onSave({
        ...config,
        credentialSets: updatedCredentials,
      });

      setEditing(null);
      setFormData({ ...EMPTY_FORM });
      setErrors({});
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key: string) => {
    if (!config) return;

    const updatedCredentials = { ...config.credentialSets };
    delete updatedCredentials[key];

    setSaving(true);
    try {
      await onSave({
        ...config,
        credentialSets: updatedCredentials,
      });
      setDeleteConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !config) {
    return <div className="text-gray-500">Loading configuration...</div>;
  }

  const usernameLabel = formData.type === 'sharepoint' ? 'App Registration Name' : 'Username';
  const usernamePlaceholder = formData.type === 'sharepoint' ? 'My App Registration' : 'Administrator';

  return (
    <div className="space-y-6">
      {/* Header with Add New button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Credential Sets</h3>
        {editing === null && (
          <button
            type="button"
            onClick={handleAddNew}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Add New
          </button>
        )}
      </div>

      {/* Add New Form (only for new credentials — edits are inline in the table) */}
      {editing === 'new' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Add New Credential Set</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="cred-name" className="block text-sm font-medium text-gray-700">Credential Set Name</label>
              <input id="cred-name" type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="CLE-SMB-Creds" />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="cred-type" className="block text-sm font-medium text-gray-700">Type</label>
              <select id="cred-type" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value as 'smb' | 'sharepoint', password: '', tenantId: '', clientId: '', clientSecret: '' })} className="mt-1 block w-full rounded-md shadow-sm sm:text-sm border-gray-300 focus:ring-blue-500 focus:border-blue-500">
                <option value="smb">SMB</option>
                <option value="sharepoint">SharePoint</option>
              </select>
            </div>
            <div>
              <label htmlFor="cred-username" className="block text-sm font-medium text-gray-700">{usernameLabel}</label>
              <input id="cred-username" type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.username ? 'border-red-300' : 'border-gray-300'}`} placeholder={usernamePlaceholder} />
              {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
            </div>
            {formData.type === 'smb' ? (
              <div>
                <label htmlFor="cred-password" className="block text-sm font-medium text-gray-700">Password</label>
                <input id="cred-password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.password ? 'border-red-300' : 'border-gray-300'}`} placeholder="Enter password" />
                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
              </div>
            ) : (
              <>
                <div>
                  <label htmlFor="cred-tenantId" className="block text-sm font-medium text-gray-700">Tenant ID</label>
                  <input id="cred-tenantId" type="text" value={formData.tenantId} onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.tenantId ? 'border-red-300' : 'border-gray-300'}`} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  {errors.tenantId && <p className="mt-1 text-sm text-red-600">{errors.tenantId}</p>}
                </div>
                <div>
                  <label htmlFor="cred-clientId" className="block text-sm font-medium text-gray-700">Client ID</label>
                  <input id="cred-clientId" type="text" value={formData.clientId} onChange={(e) => setFormData({ ...formData, clientId: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.clientId ? 'border-red-300' : 'border-gray-300'}`} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  {errors.clientId && <p className="mt-1 text-sm text-red-600">{errors.clientId}</p>}
                </div>
                <div>
                  <label htmlFor="cred-clientSecret" className="block text-sm font-medium text-gray-700">Client Secret</label>
                  <input id="cred-clientSecret" type="password" value={formData.clientSecret} onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.clientSecret ? 'border-red-300' : 'border-gray-300'}`} placeholder="Enter client secret" />
                  {errors.clientSecret && <p className="mt-1 text-sm text-red-600">{errors.clientSecret}</p>}
                </div>
              </>
            )}
          </div>
          <div className="mt-4 flex space-x-3">
            <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={handleCancel} disabled={saving} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          </div>
        </div>
      )}

      {/* Credentials List */}
      {credentials.length === 0 && editing === null ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No credential sets configured. Click "Add New" to create one.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {credentials.map(([key, cred]) => {
                const destsUsing = getDestinationsUsingCredential(key);
                const isDeleteBlocked = destsUsing.length > 0;
                const credType = (cred.type as 'smb' | 'sharepoint') || 'smb';
                const editUsernameLabel = credType === 'sharepoint' ? 'App Registration Name' : 'Username';
                const editUsernamePlaceholder = credType === 'sharepoint' ? 'My App Registration' : 'Administrator';

                return (
                  <React.Fragment key={key}>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {key}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {cred.type || 'smb'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {cred.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {cred.lastUpdated
                          ? new Date(cred.lastUpdated).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(key)}
                          disabled={editing !== null}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(key)}
                          disabled={editing !== null}
                          className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {/* Inline edit form */}
                    {editing === key && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 bg-blue-50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">{editUsernameLabel}</label>
                              <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.username ? 'border-red-300' : 'border-gray-300'}`} placeholder={editUsernamePlaceholder} />
                              {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
                            </div>
                            {credType === 'smb' ? (
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Password</label>
                                <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.password ? 'border-red-300' : 'border-gray-300'}`} placeholder="Leave empty to keep current password" />
                                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                                <p className="mt-1 text-sm text-gray-500">Leave empty to keep the current password unchanged.</p>
                              </div>
                            ) : (
                              <>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">Tenant ID</label>
                                  <input type="text" value={formData.tenantId} onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.tenantId ? 'border-red-300' : 'border-gray-300'}`} placeholder="Leave empty to keep current value" />
                                  {errors.tenantId && <p className="mt-1 text-sm text-red-600">{errors.tenantId}</p>}
                                  <p className="mt-1 text-sm text-gray-500">Leave empty to keep the current value unchanged.</p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">Client ID</label>
                                  <input type="text" value={formData.clientId} onChange={(e) => setFormData({ ...formData, clientId: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.clientId ? 'border-red-300' : 'border-gray-300'}`} placeholder="Leave empty to keep current value" />
                                  {errors.clientId && <p className="mt-1 text-sm text-red-600">{errors.clientId}</p>}
                                  <p className="mt-1 text-sm text-gray-500">Leave empty to keep the current value unchanged.</p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">Client Secret</label>
                                  <input type="password" value={formData.clientSecret} onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.clientSecret ? 'border-red-300' : 'border-gray-300'}`} placeholder="Leave empty to keep current value" />
                                  {errors.clientSecret && <p className="mt-1 text-sm text-red-600">{errors.clientSecret}</p>}
                                  <p className="mt-1 text-sm text-gray-500">Leave empty to keep the current value unchanged.</p>
                                </div>
                              </>
                            )}
                          </div>
                          <div className="mt-3 flex space-x-3">
                            <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">{saving ? 'Saving...' : 'Save'}</button>
                            <button type="button" onClick={handleCancel} disabled={saving} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Delete confirmation / blocked message */}
                    {deleteConfirm === key && (
                      <tr>
                        <td colSpan={5} className="px-6 py-3">
                          {isDeleteBlocked ? (
                            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md" role="alert">
                              <p className="text-sm font-medium">Cannot delete this credential set</p>
                              <p className="text-sm mt-1">
                                This credential set is currently referenced by the following destinations:{' '}
                                <span className="font-medium">{destsUsing.join(', ')}</span>.
                                Please update these destinations before deleting.
                              </p>
                              <button
                                type="button"
                                onClick={() => setDeleteConfirm(null)}
                                className="mt-2 text-sm font-medium text-yellow-800 underline hover:text-yellow-900"
                              >
                                Dismiss
                              </button>
                            </div>
                          ) : (
                            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md" role="alert">
                              <p className="text-sm">
                                Are you sure you want to delete <span className="font-medium">{key}</span>?
                              </p>
                              <div className="mt-2 flex space-x-3">
                                <button
                                  type="button"
                                  onClick={() => handleDelete(key)}
                                  disabled={saving}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                >
                                  {saving ? 'Deleting...' : 'Confirm Delete'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeleteConfirm(null)}
                                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CredentialsTab;
